import React, { useState, useEffect, useRef, memo, useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { Button, Input, Spin, Typography, Space, Modal, Empty, InputNumber, notification, Badge, Tag, Select } from 'antd';

import './preauth.css';
import './pa-action-dashboard.css';
import { ADMIN_REVIEW_MODAL_CLASS, roundMoney } from '../dashboard/utils';
import { CheckOutlined, CloseOutlined, QuestionCircleOutlined, ArrowLeftOutlined, WarningOutlined, ReloadOutlined, DownOutlined, RightOutlined, MedicineBoxOutlined, MessageOutlined } from "@ant-design/icons";
import lib, { myRequestObj } from "../../assets/utils/lib";
import { apiRequest } from "../notify";
import {
  createItemFlagsApi,
  useEncounterItemFlags,
  ItemFlagTag,
  ItemFlagsList,
} from "./ItemFlags";

// Flags on PA items from every actor (facility, enrollee, staff): one list per encounter.
const itemFlagsApi = createItemFlagsApi(apiRequest);
import { usePermissions } from "../../assets/utils/usePermissions";
import PopConfirm from "../compoinents/PopConfirm";
import { uri_img } from "../../assets/utils/http-request";
import EnrolleeIdentityStrip, {
  formatEnrolleeDisplayName,
  formatEnrolleeAge,
  formatEnrolleeMemberStatus,
  resolveEnrolleePlanLabel,
  resolveEnrolleeOrgName,
  resolveEnrolleeInsuranceNo,
  formatEnrolleeDateTime,
} from "../compoinents/EnrolleeIdentityStrip";
import { EnrolleeConsumption } from "../compoinents/EnrolleeConsumption";
import PACommentsDisplay from "../../components/Claims/PACommentsDisplay";
import PmPaPanel from "../../components/PlanManagement/PmPaPanel";
import PmMappingPanel from "../../components/PlanManagement/PmMappingPanel";
import useSaaSProAgentDecisions from "../../hooks/useSaaSProAgentDecisions";
import SaaSProAdvisoryChip from "../../components/SaaSPro/SaaSProAdvisoryChip";
import { formatTimeAgo, recommendationLabel } from "../../components/SaaSPro/SaaSProAdvisoryShared";
import CurrencyFormat from "react-currency-format";
import { tatFromRecord } from "../../utils/turnaroundTime";
import {
  getPaLineItemId,
  getPaLineItemStatus,
  isPaLineItemActionable,
  paLineItemName,
  formatPaLineItemClinicalLine,
  paLineItemDetailFields,
  getPaLineItemFiledAt,
  getPaLineItemProcessedAt,
  getPaLineItemReceivedAt,
  formatPaTurnaroundTime,
  formatPaLineItemTurnaround,
  formatPaDateTime,
} from "./paDisplayUtils";
import { resolveCheckinTypeLabel } from "./checkinTypeLabel";
import PAProcessingHistory from "./PAProcessingHistory";
import { usePageHeader } from "../../contexts/PageHeaderContext";

const { TextArea } = Input;
const PA_BATCH_DENSITY_KEY = 'paBatchDensityMode';
const PA_ENCOUNTER_COLLAB_KEY = 'paEncounterCollabEnabled';
const PA_ENCOUNTER_COLLAB_ENV = (typeof process !== 'undefined' && process?.env?.REACT_APP_PA_ENCOUNTER_COLLAB)
  ? process.env.REACT_APP_PA_ENCOUNTER_COLLAB
  : 'true';
const PA_ENCOUNTER_COLLAB_DEFAULT = String(PA_ENCOUNTER_COLLAB_ENV).toLowerCase() !== 'false';
/** Poll paList only while pending items exist and tab is visible. */
const PA_LIST_POLL_INTERVAL_MS = 30000;
/** Soft warn when approved qty or cost is more than this multiple of requested. */
const PA_OVER_APPROVE_RATIO = 2;

const formatReviewNaira = (value) =>
  `₦${roundMoney(value || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const enrolleeInitials = (client) => {
  const surname = String(client?.surname || "").trim();
  const first = String(client?.first_name || "").trim();
  const letters = `${surname.charAt(0)}${first.charAt(0)}`.toUpperCase();
  return letters || "?";
};

const aiChipForDecision = (decision, awaiting) => {
  if (awaiting) return { label: "AI pending", kind: "pending" };
  const rec = decision?.recommendation;
  if (!rec) return null;
  if (rec === "reject") return { label: "AI reject", kind: "reject" };
  if (rec === "query") return { label: "AI query", kind: "query" };
  if (rec === "partial_approve") return { label: "AI partial", kind: "partial" };
  return { label: "AI approve", kind: "approve" };
};

const formatSignedNaira = (delta) => {
  const rounded = roundMoney(delta || 0);
  if (rounded === 0) return null;
  const abs = Math.abs(rounded).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return `${rounded > 0 ? '+' : '−'}₦${abs}`;
};

const isPaLineItemFlagged = (item) => (
  Number(item?.has_active_flags) > 0 || Number(item?.flags_active_count) > 0
);

const itemMatchesWorkbenchFilter = (item, draft, filter) => {
  if (filter === 'all') return true;
  if (filter === 'flagged') return isPaLineItemFlagged(item);
  const statusNum = getPaLineItemStatus(item);
  const decision = draft?.decision;
  if (filter === 'accepted') return decision === 'accept' || statusNum === 1;
  if (filter === 'rejected') return decision === 'reject' || statusNum === 3;
  if (filter === 'pending') {
    if (decision === 'accept' || decision === 'reject') return false;
    if (statusNum === 1 || statusNum === 3) return false;
    return true;
  }
  return true;
};

const getEncounterCommentCount = (client) => {
  if (Array.isArray(client?.pa_comments) && client.pa_comments.length) {
    return client.pa_comments.length;
  }
  if (client?.pa_comment || client?.batch_comment || client?.pa_batch_comment || client?.pa_request_comment) {
    return 1;
  }
  return 0;
};

const formatApprovalDiff = (approvedQty, approvedCost, requestedQty, requestedCost) => {
  const qtyDelta = Math.floor(Number(approvedQty) || 0) - Math.floor(Number(requestedQty) || 0);
  const costDelta = roundMoney((Number(approvedCost) || 0) - (Number(requestedCost) || 0));
  if (qtyDelta === 0 && costDelta === 0) {
    return { label: 'Same as requested', isSame: true, overApprove: false };
  }
  const parts = [];
  if (qtyDelta !== 0) {
    parts.push(`${qtyDelta > 0 ? '+' : ''}${qtyDelta} qty`);
  }
  const money = formatSignedNaira(costDelta);
  if (money) parts.push(money);
  const reqQty = Math.max(1, Number(requestedQty) || 1);
  const reqCost = Number(requestedCost) || 0;
  const overApprove = (
    (Number(approvedQty) || 0) > reqQty * PA_OVER_APPROVE_RATIO
    || (reqCost > 0 && (Number(approvedCost) || 0) > reqCost * PA_OVER_APPROVE_RATIO)
  );
  return { label: parts.join(' · '), isSame: false, overApprove };
};

const PARequestHook = () => {
  const { setPageHeader } = usePageHeader();
  const { canAccess, hasAnyPermission } = usePermissions();
  const [items, setItems] = useState([]);
  const [provDiagenosis, setProvDiagenosis] = useState([]);
  const [client, setClient] = useState({});
  const navigator = useNavigate();
  const location = useLocation();
  const state = location.state;
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const checkinId = params.get('checkin') || state?.checkinId || state?.item?.code || null;
  const itemFlagState = useEncounterItemFlags(itemFlagsApi, checkinId);
  const status = params.get('status');
  const pollingRef = useRef(null);
  const [pmPanelRefresh, setPmPanelRefresh] = useState(0);
  const fetchInFlightRef = useRef(false);

  const [showAddItemButton, showRemoveItemButton] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [decisionDrafts, setDecisionDrafts] = useState({});
  const [overallComment, setOverallComment] = useState('');
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [failedDecisionIds, setFailedDecisionIds] = useState([]);
  const [densityMode, setDensityMode] = useState(() => {
    try {
      const savedDensity = localStorage.getItem(PA_BATCH_DENSITY_KEY);
      if (savedDensity === 'compact' || savedDensity === 'comfortable') return savedDensity;
    } catch (_e) {
      /* ignore */
    }
    return 'compact';
  });
  const [itemFilter, setItemFilter] = useState('all');
  const [focusedItemIndex, setFocusedItemIndex] = useState(0);
  const [openRationales, setOpenRationales] = useState({});
  const [commentsCollapsed, setCommentsCollapsed] = useState(true);
  const [encounterCollabEnabled, setEncounterCollabEnabled] = useState(PA_ENCOUNTER_COLLAB_DEFAULT);
  const [diagnosisModalOpen, setDiagnosisModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [advisorySeed, setAdvisorySeed] = useState({ decisions: null, pending: undefined });

  useEffect(() => {
    const savedCollabToggle = localStorage.getItem(PA_ENCOUNTER_COLLAB_KEY);
    if (PA_ENCOUNTER_COLLAB_DEFAULT && savedCollabToggle !== null) {
      setEncounterCollabEnabled(!(savedCollabToggle === 'false' || savedCollabToggle === '0'));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(PA_BATCH_DENSITY_KEY, densityMode);
  }, [densityMode]);

  useEffect(() => {
    if (PA_ENCOUNTER_COLLAB_DEFAULT) {
      localStorage.setItem(PA_ENCOUNTER_COLLAB_KEY, String(encounterCollabEnabled));
    }
  }, [encounterCollabEnabled]);

  const clearPaListPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const goBack = useCallback(() => {
    clearPaListPolling();
    if (state?.fromPaAnalyticsEncounters) {
      navigator('/dashboard/preauth', {
        state: { openPaAnalyticsDetailTab: 'encounters' },
      });
      return;
    }

    // Encounters queue sends its full URL (search, filters, sort, page) so we
    // return to the exact view the reviewer left.
    if (state?.queueUrl) {
      navigator(state.queueUrl);
      return;
    }

    if (state?.item) {
      navigator('/checkins', { state: { item: state.item, filterKeyword: state.filterKeyword } });
      return;
    }

    if (status) {
      navigator(`/preauth?s=${status}`);
      return;
    }

    navigator(-1);
  }, [navigator, state, status, clearPaListPolling]);

  const fetchPAData = useCallback(async (claimsId) => {
    if (!claimsId) return undefined;
    return myRequestObj('paList').post({ checkinId: claimsId })
      .then(response => response?.data)
      .then(body => {
        const provList = body?.data?.diagnosis?.map((d) => ({
          label: `${d.code} ${d.name}`,
          value: d.id,
        }));
        setProvDiagenosis(provList ? provList : []);
        return body?.data;
      }).catch(e => {
        console.log('PA Fetch Error', e.message);
        return undefined;
      });
  }, []);

  const applyPaListResponse = useCallback((res) => {
    if (!res) return;
    setItems(res?.items || []);
    const seeded = Array.isArray(res?.agent_decisions)
      ? res.agent_decisions
      : (res?.items || []).map((it) => it?.ai_decision).filter(Boolean);
    setAdvisorySeed({
      decisions: seeded,
      pending: res?.agent_pending,
    });
    const clientData = res?.misc || {};
    const facilityId = clientData?.facility_id || clientData?.facilityId ||
      state?.item?.facility_id || state?.item?.facilityId;
    const facilityName = clientData?.facility_name || clientData?.facilityName ||
      state?.item?.facility_name || state?.item?.facilityName;
    const checkinTypeLabel = resolveCheckinTypeLabel({
      ...state?.item,
      ...clientData,
    });

    setClient({
      ...clientData,
      first_name: clientData?.first_name || state?.item?.first_name,
      middle_name: clientData?.middle_name || state?.item?.middle_name,
      surname: clientData?.surname || state?.item?.surname,
      insurance_no: clientData?.insurance_no || state?.item?.insurance_no,
      facility_id: facilityId,
      facilityId: facilityId,
      facility_name: facilityName,
      facilityName: facilityName,
      checkinType: clientData?.checkinType ?? state?.item?.checkinType,
      checkin_type_name: checkinTypeLabel || clientData?.checkin_type_name || state?.item?.checkin_type_name,
      checkinName: checkinTypeLabel || clientData?.checkinName || state?.item?.checkinName,
    });
  }, [state?.item]);

  const refreshPaList = useCallback(async () => {
    if (!checkinId || fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    setRefreshing(true);
    try {
      const res = await fetchPAData(checkinId);
      applyPaListResponse(res);
    } finally {
      fetchInFlightRef.current = false;
      setRefreshing(false);
    }
  }, [applyPaListResponse, checkinId, fetchPAData]);

  /** True when this encounter is no longer active at the facility (checked out). */
  const hasCheckoutDate = Boolean(
    client?.checkout_date || state?.item?.checkout_date
  );
  /**
   * PA is closed for decisions. Automatic checkout sets checkout_date without
   * filing the claim, so the API's `pa_open` keeps those encounters decidable
   * until the claim is submitted. Without the flag (older API) any checkout locks.
   */
  const isEncounterCheckedOut = hasCheckoutDate && client?.pa_open !== true;

  const hasPendingPaItems = useMemo(() => {
    if (!(items || []).length) return true;
    return (items || []).some((item) => isPaLineItemActionable(item));
  }, [items]);

  const shouldPollPaList = Boolean(checkinId) && !isEncounterCheckedOut && hasPendingPaItems;

  useEffect(() => {
    setAdvisorySeed({ decisions: null, pending: undefined });
  }, [checkinId]);

  useEffect(() => {
    if (!checkinId) return undefined;
    let cancelled = false;
    fetchInFlightRef.current = true;
    fetchPAData(checkinId)
      .then((res) => {
        if (!cancelled) applyPaListResponse(res);
      })
      .finally(() => {
        fetchInFlightRef.current = false;
      });
    return () => {
      cancelled = true;
      clearPaListPolling();
    };
  }, [checkinId, applyPaListResponse, clearPaListPolling, fetchPAData]);

  useEffect(() => {
    clearPaListPolling();
    if (!shouldPollPaList) return undefined;

    const tick = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (window.location.pathname !== '/preauth-action') return;
      refreshPaList();
    };

    pollingRef.current = setInterval(tick, PA_LIST_POLL_INTERVAL_MS);

    const onVisibility = () => {
      if (document.hidden) {
        clearPaListPolling();
        return;
      }
      if (shouldPollPaList) {
        tick();
        clearPaListPolling();
        pollingRef.current = setInterval(tick, PA_LIST_POLL_INTERVAL_MS);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      clearPaListPolling();
    };
  }, [shouldPollPaList, clearPaListPolling, refreshPaList]);

  const send_claim = () => {
    clearPaListPolling();
    goBack();
  };

  /** SaaSPro AI advisory overlay — read-only today. Polls while pending. */
  const saaspro = useSaaSProAgentDecisions(checkinId || client?.code || null, {
    claimItemIds: (items || []).map(getPaLineItemId).filter((v) => v != null),
    seedDecisions: advisorySeed.decisions,
    seedPending: advisorySeed.pending,
  });
  const refreshSaasproAdvisories = saaspro.refresh;
  const itemsWithDecisionCount = (items || []).reduce((acc, it) => {
    const itemId = getPaLineItemId(it);
    const hasAi = itemId != null && (
      saaspro.decisionsByItem[String(itemId)] || it?.ai_decision
    );
    return acc + (hasAi ? 1 : 0);
  }, 0);
  const overallFromItems = useMemo(() => {
    const hit = (items || []).map((it) => it?.ai_decision).find((d) => d?.overall_recommendation);
    if (!hit) return null;
    return {
      recommendation: hit.overall_recommendation,
      rationale: hit.overall_rationale,
      received_at: hit.received_at,
    };
  }, [items]);

  // Admitting is a state transition, not a permission-gated action — any PA that
  // hasn't been checked out yet can be converted, regardless of the viewer's role.
  const canAdmitEnrollee = !hasCheckoutDate;
  const canAddMorePa = hasAnyPermission(['preauth.create', 'preauth.manual'])
    && Number(state?.item?.source) === 1;

  const handleAdmitEnrollee = async () => {
    const encounterId = checkinId || client?.code;
    if (!encounterId || !canAdmitEnrollee) return;

    try {
      const resp = await lib.admitEnrollee(encounterId);
      const body = resp?.data || resp;

      if (Number(body?.error)) {
        notification.error({
          message: body?.message || 'Failed to admit enrollee',
          placement: 'topLeft',
        });
        return;
      }

      notification.success({
        message: body?.message || 'Enrollee admitted (inpatient).',
        placement: 'topLeft',
      });
    } catch (e) {
      notification.error({
        message: e?.response?.data?.message || e?.message || 'Failed to admit enrollee',
        placement: 'topLeft',
      });
    }
  };

  const handleAddItems = () => {
    // Ensure facility_id is passed from either client, state.item, or encounter data
    const facilityId = client?.facility_id || client?.facilityId || state?.item?.facility_id || state?.item?.facilityId;
    const facilityName = client?.facility_name || client?.facilityName || state?.item?.facility_name || state?.item?.facilityName;
    
    // Get checkin type info
    const checkinTypeId = client?.checkinType || state?.item?.checkinType;
    const checkinTypeName = client?.checkinName || client?.checkin_type_name || state?.item?.checkin_type_name;
    
    // Pass client data with facility information included
    const stateData = {
      ...client,
      facility_id: facilityId,
      facilityId: facilityId, // Support both naming conventions
      facility_name: facilityName,
      facilityName: facilityName,
      insurance_no: client?.insurance_no || state?.item?.insurance_no,
      checkinType: checkinTypeId,
      checkin_type_id: checkinTypeId,
      checkin_type_name: checkinTypeName,
    };
    
    navigator(`/file-encounter?eid=${client?.code}`, { state: stateData })
    // setShowAddItem(!!!showAddItem)
  }

  // Catalog module preauthApproval (decide / approve alias) — not a local slug list.
  const canDraftDecisions = canAccess('preauthApproval')
    && !isEncounterCheckedOut;
  const isItemActionable = useCallback((item) => isPaLineItemActionable(item), []);

  const handleDecisionDraftChange = useCallback((itemId, patch) => {
    const key = String(itemId);
    setDecisionDrafts(prev => {
      const current = prev[key] || {};
      const merged = { ...current, ...patch };
      return { ...prev, [key]: merged };
    });
  }, []);

  const handleClearDecision = useCallback((itemId) => {
    const key = String(itemId);
    setDecisionDrafts(prev => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleClearAllDecisions = useCallback(() => {
    setDecisionDrafts({});
    setFailedDecisionIds([]);
    setOverallComment('');
  }, []);

  const handleMarkAllAccepted = useCallback(() => {
    if (!canDraftDecisions) return;
    const actionableItems = (items || []).filter(isItemActionable);
    if (!actionableItems.length) return;
    const updates = actionableItems.reduce((acc, item) => {
      const key = String(getPaLineItemId(item));
      const requestedQty = Math.max(1, Number(item?.quantity) || 1);
      const fallbackCost = roundMoney(
        Number(item?.approved_cost) > 0 ? Number(item?.approved_cost) : Number(item?.cost) || 0
      );
      acc[key] = {
        decision: 'accept',
        approved_quantity: requestedQty,
        approved_cost: fallbackCost,
        comment: decisionDrafts[key]?.comment || '',
      };
      return acc;
    }, {});
    setDecisionDrafts(prev => ({ ...prev, ...updates }));
    setFailedDecisionIds([]);
  }, [canDraftDecisions, decisionDrafts, isItemActionable, items]);

  const handleMarkAllRejected = useCallback(() => {
    if (!canDraftDecisions) return;
    const actionableItems = (items || []).filter(isItemActionable);
    if (!actionableItems.length) return;

    const applyRejectAll = (reason) => {
      const updates = actionableItems.reduce((acc, item) => {
        const key = String(getPaLineItemId(item));
        acc[key] = {
          decision: 'reject',
          approved_cost: undefined,
          approved_quantity: undefined,
          comment: reason || decisionDrafts[key]?.comment || '',
        };
        return acc;
      }, {});
      setDecisionDrafts(prev => ({ ...prev, ...updates }));
      setFailedDecisionIds([]);
    };

    if ((overallComment || '').trim()) {
      applyRejectAll(overallComment.trim());
      return;
    }

    let reasonDraft = '';
    Modal.confirm({
      title: 'Reject all pending items',
      icon: null,
      content: (
        <div>
          <p>
            This reason is applied to all {actionableItems.length} pending item{actionableItems.length === 1 ? '' : 's'}
            {' '}(edit any item individually afterward if it needs a different note).
          </p>
          <TextArea
            rows={3}
            placeholder="Reason for rejection (required)"
            onChange={(e) => { reasonDraft = e.target.value; }}
          />
        </div>
      ),
      okText: 'Reject all',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: () => {
        if (!reasonDraft.trim()) {
          notification.error({ message: 'Reason required', description: 'Add a reason before rejecting all items.', placement: 'topLeft' });
          return Promise.reject();
        }
        applyRejectAll(reasonDraft.trim());
      },
    });
  }, [canDraftDecisions, decisionDrafts, isItemActionable, items, overallComment]);

  const itemFilterCounts = useMemo(() => {
    const counts = { all: 0, pending: 0, accepted: 0, rejected: 0, flagged: 0 };
    (items || []).forEach((item) => {
      const itemId = getPaLineItemId(item);
      const draft = itemId != null ? decisionDrafts[String(itemId)] : undefined;
      counts.all += 1;
      if (itemMatchesWorkbenchFilter(item, draft, 'pending')) counts.pending += 1;
      if (itemMatchesWorkbenchFilter(item, draft, 'accepted')) counts.accepted += 1;
      if (itemMatchesWorkbenchFilter(item, draft, 'rejected')) counts.rejected += 1;
      if (itemMatchesWorkbenchFilter(item, draft, 'flagged')) counts.flagged += 1;
    });
    return counts;
  }, [decisionDrafts, items]);

  const visibleIndexedItems = useMemo(
    () => (items || [])
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => {
        const itemId = getPaLineItemId(item);
        const draft = itemId != null ? decisionDrafts[String(itemId)] : undefined;
        return itemMatchesWorkbenchFilter(item, draft, itemFilter);
      }),
    [decisionDrafts, itemFilter, items],
  );

  const draftSummary = (items || []).reduce((acc, item) => {
    const itemId = getPaLineItemId(item);
    const statusNum = getPaLineItemStatus(item);
    const actionable = isItemActionable(item);
    const draft = itemId != null ? decisionDrafts[String(itemId)] : null;
    const requestedCost = roundMoney(Number(item?.cost || 0));
    acc.requestedTotal = roundMoney(acc.requestedTotal + requestedCost);
    acc.itemCount += 1;

    if (actionable && draft?.decision === 'accept') {
      acc.accepted += 1;
      acc.approvedTotal = roundMoney(acc.approvedTotal + roundMoney(draft?.approved_cost || 0));
      return acc;
    }
    if (actionable && draft?.decision === 'reject') {
      acc.rejected += 1;
      return acc;
    }
    if (statusNum === 1) {
      acc.accepted += 1;
      acc.approvedTotal = roundMoney(
        acc.approvedTotal + roundMoney(Number(item?.approved_cost ?? item?.cost ?? 0)),
      );
      return acc;
    }
    if (statusNum === 3) {
      acc.rejected += 1;
      return acc;
    }

    acc.pending += 1;
    return acc;
  }, { accepted: 0, rejected: 0, pending: 0, approvedTotal: 0, requestedTotal: 0, itemCount: 0 });
  const hasDraftSelections = Object.values(decisionDrafts || {}).some((d) => Boolean(d?.decision));
  const formatSummaryMoney = (value) => roundMoney(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const memberId = client?.insurance_no || client?.member_id || client?.memberId || '--';
  const hasMemberId = memberId !== '--';
  const providerName = client?.facility_name || client?.facilityName || state?.item?.facility_name || '--';
  // Mirrors PACommentsDisplay's own comment extraction so the toggle count matches what it renders.
  const encounterCommentCount = useMemo(() => {
    const list = client?.pa_comments;
    if (Array.isArray(list) && list.length > 0) return list.length;
    return (client?.pa_comment || client?.batch_comment) ? 1 : 0;
  }, [client]);
  const diagnosisCodes = (provDiagenosis || []).map((d) => {
    const rawLabel = String(d?.label || '').trim();
    const parts = rawLabel.split(' ');
    const code = parts.shift() || '--';
    const description = parts.join(' ') || rawLabel;
    return { code, description };
  });
  const diagnosisPreview = diagnosisCodes.slice(0, 3);

  const handleSubmitBatchDecisions = useCallback(async () => {
    if (!canDraftDecisions || batchSubmitting) return;
    const encounterId = checkinId || client?.code;
    if (!encounterId) return;

    const actionableMap = (items || []).reduce((acc, item) => {
      const itemId = getPaLineItemId(item);
      if (itemId != null && isItemActionable(item)) acc[String(itemId)] = item;
      return acc;
    }, {});

    const decisions = Object.entries(decisionDrafts || {})
      .filter(([itemId, draft]) => actionableMap[itemId] && draft?.decision)
      .map(([itemId, draft]) => {
        const individualComment = (draft?.comment || '').trim();
        const base = {
          item_id: Number(itemId),
          decision: draft.decision,
          // A rejected item with no note of its own falls back to the shared
          // submission note, so rejecting many items doesn't require opening
          // each one just to type the same reason.
          comment: individualComment
            || (draft.decision === 'reject' ? (overallComment || '').trim() : '')
            || undefined,
        };
        if (draft.decision === 'accept') {
          const item = actionableMap[itemId];
          const requestedQty = Math.max(1, Number(item?.quantity) || 1);
          const approvedQty = Math.max(
            1,
            Number(draft?.approved_quantity) > 0 ? Number(draft.approved_quantity) : requestedQty
          );
          base.approved_quantity = approvedQty;
          base.approved_cost = roundMoney(draft?.approved_cost || 0);
        }
        return base;
      });

    if (!decisions.length) {
      notification.warning({
        message: 'No decisions selected',
        description: 'Mark every item as accept or reject before submitting.',
        placement: 'topLeft',
      });
      return;
    }

    const leftoverPending = Object.keys(actionableMap).filter((id) => {
      const draft = decisionDrafts[id];
      return !draft?.decision;
    }).length;
    if (leftoverPending > 0) {
      notification.warning({
        message: 'Items still pending',
        description: `${leftoverPending} item${leftoverPending === 1 ? '' : 's'} still need a decision. Submit is one batch — no partial sends.`,
        placement: 'topLeft',
      });
      return;
    }

    const missingRejectReason = decisions.filter((d) => d.decision === 'reject' && !(d.comment || '').trim());
    if (missingRejectReason.length) {
      notification.error({
        message: 'Reject reason required',
        description: `Add a reason for ${missingRejectReason.length} rejected item${missingRejectReason.length === 1 ? '' : 's'}.`,
        placement: 'topLeft',
      });
      return;
    }

    if (itemFilter !== 'all') {
      const visibleIds = new Set(
        (items || [])
          .filter((item) => {
            const itemId = getPaLineItemId(item);
            const draft = itemId != null ? decisionDrafts[String(itemId)] : undefined;
            return itemMatchesWorkbenchFilter(item, draft, itemFilter);
          })
          .map((item) => String(getPaLineItemId(item)))
          .filter((id) => id && id !== 'null' && id !== 'undefined')
      );
      const hiddenDraftCount = decisions.filter((d) => !visibleIds.has(String(d.item_id))).length;
      if (hiddenDraftCount > 0) {
        const confirmed = await new Promise((resolve) => {
          Modal.confirm({
            title: 'Submit includes hidden items',
            content: `${hiddenDraftCount} decision${hiddenDraftCount === 1 ? '' : 's'} ${hiddenDraftCount === 1 ? 'is' : 'are'} on items hidden by the current filter. Submit will still include them.`,
            okText: 'Submit all',
            cancelText: 'Cancel',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!confirmed) return;
      }
    }

    const overApproveCount = decisions.filter((d) => {
      if (d.decision !== 'accept') return false;
      const item = actionableMap[String(d.item_id)];
      if (!item) return false;
      return formatApprovalDiff(
        d.approved_quantity,
        d.approved_cost,
        Math.max(1, Number(item.quantity) || 1),
        Number(item.cost) || 0,
      ).overApprove;
    }).length;

    const acceptedCount = decisions.filter((d) => d.decision === 'accept').length;
    const rejectedCount = decisions.filter((d) => d.decision === 'reject').length;
    const approvedSum = decisions
      .filter((d) => d.decision === 'accept')
      .reduce((sum, d) => sum + roundMoney(d.approved_cost || 0), 0);
    const confirmedSubmit = await new Promise((resolve) => {
      Modal.confirm({
        title: 'Submit decisions?',
        content: `${acceptedCount} accepted for ${formatReviewNaira(approvedSum)}, ${rejectedCount} rejected${overApproveCount ? `. ${overApproveCount} item${overApproveCount === 1 ? '' : 's'} are more than twice the requested amount` : ''}. This is sent as one batch and the facility is notified immediately.`,
        okText: 'Submit',
        cancelText: 'Keep reviewing',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmedSubmit) return;

    const invalidAccepts = decisions.filter(d => d.decision === 'accept' && !(Number(d?.approved_cost) > 0));
    if (invalidAccepts.length) {
      notification.error({
        message: 'Invalid approved cost',
        description: 'All accepted items must have an approved cost greater than zero.',
        placement: 'topLeft',
      });
      return;
    }

    setBatchSubmitting(true);
    try {
      const idempotencyKey = `${encounterId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const response = await lib.submitPABatchDecisions(encounterId, {
        overall_comment: overallComment?.trim() || undefined,
        decisions,
      }, { idempotencyKey });
      const body = response?.data;

      if (!body || Number(body?.error)) {
        notification.error({
          message: 'Batch submission failed',
          description: body?.message || 'Unable to submit batch decisions right now.',
          placement: 'topLeft',
        });
        return;
      }

      const results = body?.data?.results || [];
      const failedIds = results
        .filter(r => !r?.ok)
        .map(r => String(r?.item_id))
        .filter(Boolean);
      const succeededIds = results
        .filter(r => Boolean(r?.ok))
        .map(r => String(r?.item_id))
        .filter(Boolean);

      setFailedDecisionIds(failedIds);
      if (succeededIds.length) {
        setDecisionDrafts(prev => {
          const next = { ...prev };
          succeededIds.forEach((id) => delete next[id]);
          return next;
        });
      }

      if (!failedIds.length) {
        setOverallComment('');
      }

      const summary = body?.data?.summary || {};
      notification.success({
        message: 'Batch decisions submitted',
        description: `Processed ${summary?.total_received || decisions.length}: accepted ${summary?.accepted || 0}, rejected ${summary?.rejected || 0}, failed ${summary?.failed || 0}.`,
        placement: 'topLeft',
      });

      const refreshed = await fetchPAData(encounterId);
      applyPaListResponse(refreshed);
      refreshSaasproAdvisories();
      setPmPanelRefresh((n) => n + 1);
    } catch (e) {
      notification.error({
        message: 'Batch submission failed',
        description: e?.response?.data?.message || e?.message || 'Unable to submit batch decisions right now.',
        placement: 'topLeft',
      });
    } finally {
      setBatchSubmitting(false);
    }
  }, [applyPaListResponse, batchSubmitting, canDraftDecisions, checkinId, client?.code, decisionDrafts, fetchPAData, isItemActionable, itemFilter, items, overallComment, refreshSaasproAdvisories]);

  const applyAcceptDraft = useCallback((item) => {
    if (!canDraftDecisions || !isItemActionable(item)) return;
    const itemId = getPaLineItemId(item);
    if (itemId == null) return;
    const qtySafe = Math.max(1, Number(item?.quantity) || 1);
    const fallbackCost = roundMoney(
      Number(item?.approved_cost) > 0 ? Number(item.approved_cost) : Number(item?.cost) || 0
    );
    handleDecisionDraftChange(itemId, {
      decision: 'accept',
      approved_quantity: qtySafe,
      approved_cost: fallbackCost,
    });
  }, [canDraftDecisions, handleDecisionDraftChange, isItemActionable]);

  const applyRejectDraft = useCallback((item) => {
    if (!canDraftDecisions || !isItemActionable(item)) return;
    const itemId = getPaLineItemId(item);
    if (itemId == null) return;
    handleDecisionDraftChange(itemId, {
      decision: 'reject',
      approved_cost: undefined,
      approved_quantity: undefined,
    });
  }, [canDraftDecisions, handleDecisionDraftChange, isItemActionable]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (!canDraftDecisions) return;
      const list = visibleIndexedItems;
      if (!list.length) return;
      const k = e.key.toLowerCase();
      if (k === 'j') {
        e.preventDefault();
        setFocusedItemIndex((i) => Math.min(list.length - 1, i + 1));
      } else if (k === 'k') {
        e.preventDefault();
        setFocusedItemIndex((i) => Math.max(0, i - 1));
      } else if (k === 'a' || k === 'r') {
        const row = list[Math.min(focusedItemIndex, list.length - 1)];
        if (!row) return;
        if (k === 'a') applyAcceptDraft(row.item);
        else applyRejectDraft(row.item);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [applyAcceptDraft, applyRejectDraft, canDraftDecisions, focusedItemIndex, visibleIndexedItems]);

  const checkinTypeLabel = resolveCheckinTypeLabel(client) || resolveCheckinTypeLabel(state?.item);
  const displayName = formatEnrolleeDisplayName(client);
  const age = formatEnrolleeAge(client);
  const planLabel = resolveEnrolleePlanLabel(client);
  const orgName = resolveEnrolleeOrgName(client);
  const memberStatus = formatEnrolleeMemberStatus(client);
  const insuranceNo = resolveEnrolleeInsuranceNo(client);
  const photoSrc = client?.pic_name ? `${uri_img}${client.pic_name}` : null;
  const tat = tatFromRecord(client);
  const receivedAgo = formatTimeAgo(client?.received_at || client?.checkin_date || client?.checkinDate);
  const submittedAt = formatEnrolleeDateTime(client?.checkin_date || client?.checkinDate);
  const requestedTotal = draftSummary.requestedTotal;
  const withheld = Math.max(0, requestedTotal - draftSummary.approvedTotal);
  const allDecided = draftSummary.pending === 0 && draftSummary.itemCount > 0;
  const canSubmit = canDraftDecisions && allDecided && !batchSubmitting && !isEncounterCheckedOut;
  const decidedCount = draftSummary.itemCount - draftSummary.pending;
  const overallAi = saaspro.overall || overallFromItems;
  const overallAiLabel = overallAi?.recommendation
    ? String(overallAi.recommendation).replace(/_/g, ' ')
    : (saaspro.hasPending ? 'in progress' : null);
  const statusKind = isEncounterCheckedOut ? 'closed' : (allDecided ? 'done' : 'pending');
  const statusText = isEncounterCheckedOut ? 'Checked out' : (allDecided ? 'Ready to submit' : 'Pending review');
  const enrolleeMeta = [
    age != null ? `${age} yrs` : null,
    planLabel !== '—' ? planLabel : null,
    orgName !== '—' ? orgName : null,
    providerName !== '--' ? providerName : null,
  ].filter(Boolean).join(' · ');

  /** Avoids re-triggering the effect below on every render just because
   * `send_claim` (unmemoized) gets a new reference — only the primitives
   * in the dependency array should do that. */
  const pageHeaderActionsRef = useRef({ send_claim, refreshPaList });
  pageHeaderActionsRef.current = { send_claim, refreshPaList };

  useEffect(() => {
    setPageHeader({
      left: (
        <>
          <Button onClick={() => pageHeaderActionsRef.current.send_claim()}>← Queue</Button>
          <div className="pa-review-topbar__rule" />
          <h1 className="pa-review-topbar__title">PA request review</h1>
          {client?.code ? <span className="pa-review-topbar__code" title={client.code}>{client.code}</span> : null}
        </>
      ),
      right: (
        <>
          <span className={`pa-review-status${statusKind === 'done' ? ' is-done' : ''}${statusKind === 'closed' ? ' is-closed' : ''}`}>
            <span className="pa-review-status__dot" />
            {statusText}
            {tat.label ? ` · ${tat.label}` : ''}
          </span>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => pageHeaderActionsRef.current.refreshPaList()}
            loading={refreshing}
          >
            Refresh
          </Button>
        </>
      ),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, client?.code, statusKind, statusText, tat.label, refreshing]);

  return (
    <div className="pa-action-dashboard">
      <header className="pa-review-topbar pa-review-topbar--mobile-only">
        <div className="pa-review-topbar__lead">
          <Button onClick={send_claim}>← Queue</Button>
          <h1 className="pa-review-topbar__title">PA request review</h1>
        </div>
        <div className="pa-review-topbar__actions">
          <span className={`pa-review-status${statusKind === 'done' ? ' is-done' : ''}${statusKind === 'closed' ? ' is-closed' : ''}`}>
            <span className="pa-review-status__dot" />
            {statusText}
          </span>
          <Button icon={<ReloadOutlined />} onClick={refreshPaList} loading={refreshing} />
        </div>
      </header>

      <div className="pa-review-grid">
        <main className="pa-review-main">
          <section className="pa-action-overview" aria-label="Encounter overview">
            <EnrolleeIdentityStrip
              variant="embedded"
              client={client}
              typeLabel={checkinTypeLabel}
              showMissingType
              facilityName={providerName}
              datetime={client?.checkin_date || client?.checkinDate}
              itemCount={(items || []).length}
              showConsumption={false}
              actions={(
                <>
                  {/* Consumption is the primary action here (Customer Success): the
                      client's encounters are reachable from the consumption view, and
                      the Encounters shortcut lives in the Policy card below. */}
                  {insuranceNo ? (
                    <EnrolleeConsumption
                      userId={insuranceNo}
                      enrolleeData={client}
                      triggerVariant="button"
                      triggerLabel="View Consumption"
                      triggerSize="middle"
                      triggerClassName="pa-action-overview__consumption"
                    />
                  ) : null}
                  {canAdmitEnrollee ? (
                    <PopConfirm
                      title={
                        <>
                          <strong>Admit enrollee?</strong>
                          <br />
                          <span>This will switch this encounter to inpatient.</span>
                        </>
                      }
                      onConfirm={handleAdmitEnrollee}
                      confirmType="warning"
                      triggerButtonText="Admit"
                      triggerButtonProps={{ type: 'text', size: 'small', className: 'pa-action-overview__admit' }}
                      placement="bottomRight"
                      theme="warning"
                      debounceTime={500}
                    />
                  ) : null}
                </>
              )}
            />
          </section>

          <section className="pa-action-comments" aria-label="Encounter comments">
            <button
              type="button"
              className="pa-action-comments__toggle"
              aria-expanded={!commentsCollapsed}
              onClick={() => setCommentsCollapsed((collapsed) => !collapsed)}
            >
              <span className="pa-action-comments__toggle-label">
                {commentsCollapsed ? <RightOutlined /> : <DownOutlined />}
                Comments
              </span>
              <span className="pa-action-comments__toggle-count">
                {encounterCommentCount} note{encounterCommentCount === 1 ? '' : 's'}
              </span>
            </button>
            {!commentsCollapsed ? (
              <div className="pa-action-comments__body">
                <PACommentsDisplay
                  response={client}
                  encounter={client}
                  showFullHistory={true}
                  compact={true}
                  emptyMessage="No encounter comments yet."
                />
              </div>
            ) : null}
          </section>

          <section className="pa-review-card pa-review-items">
            <div className="pa-review-diagnosis-banner">
              <div className="pa-review-diagnosis-banner__label">
                <MedicineBoxOutlined />
                <span>Diagnosis</span>
              </div>
              <div className="pa-review-diagnosis-banner__chips">
                {diagnosisPreview.length ? (
                  diagnosisPreview.map((diag, idx) => (
                    <span className="pa-review-diagnosis-banner__chip" key={`${diag.code}-${idx}`} title={diag.description}>
                      <code>{diag.code}</code>
                      <span>{diag.description}</span>
                    </span>
                  ))
                ) : (
                  <span className="pa-review-diagnosis-banner__empty">No diagnosis attached</span>
                )}
                {diagnosisCodes.length > 3 ? (
                  <button type="button" className="pa-review-diagnosis-banner__more" onClick={() => setDiagnosisModalOpen(true)}>
                    +{diagnosisCodes.length - 3} more
                  </button>
                ) : null}
              </div>
            </div>

            <div className="pa-review-items__head">
              <div className="pa-review-items__title-wrap">
                <h2>Request items</h2>
                <span className="pa-review-items__decided">{decidedCount} of {draftSummary.itemCount} decided</span>
              </div>
              <div className="pa-review-items__tools">
                <div className="pa-review-seg" role="tablist" aria-label="Item filters">
                  {[
                    ['all', `All ${itemFilterCounts.all}`],
                    ['pending', `Pending ${itemFilterCounts.pending}`],
                    ['accepted', `Accepted ${itemFilterCounts.accepted}`],
                    ['rejected', `Rejected ${itemFilterCounts.rejected}`],
                    ['flagged', `Flagged ${itemFilterCounts.flagged}`],
                  ].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={itemFilter === key ? 'is-active' : ''}
                      onClick={() => setItemFilter(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <Button onClick={() => setDensityMode((mode) => mode === 'compact' ? 'comfortable' : 'compact')}>
                  {densityMode === 'compact' ? 'Comfortable view' : 'Compact view'}
                </Button>
              </div>
            </div>

            {overallAiLabel || saaspro.hasPending ? (
              <div className="pa-review-ai">
                <span className="pa-review-ai__label"><span className="pa-review-ai__mark" />AI review</span>
                <span>
                  {overallAiLabel
                    ? `${overallAiLabel}${overallAi?.rationale ? ` — ${overallAi.rationale}` : ''}`
                    : 'Analysing this encounter…'}
                </span>
                {receivedAgo ? <span className="pa-review-ai__ago">received {receivedAgo}</span> : null}
              </div>
            ) : null}

            {visibleIndexedItems.length === 0 ? (
              <div style={{ padding: 28 }}>
                <Empty description={(items || []).length ? 'No items match this filter' : 'No request items'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            ) : visibleIndexedItems.map(({ item, index }, visibleIndex) => {
              const itemId = getPaLineItemId(item);
              const itemKey = itemId != null ? String(itemId) : `row-${index}`;
              return (
                <AuthorizationRequestDataListHook
                  data={item}
                  key={itemKey}
                  itemId={itemId}
                  itemFlags={itemId != null ? itemFlagState.byItem[itemId] : undefined}
                  serialNumber={index + 1}
                  compact={densityMode === 'compact'}
                  isFocused={visibleIndex === focusedItemIndex}
                  onFocusRow={() => setFocusedItemIndex(visibleIndex)}
                  rationaleOpen={itemId != null && Boolean(openRationales[String(itemId)])}
                  onToggleRationale={() => {
                    if (itemId == null) return;
                    setOpenRationales((prev) => ({ ...prev, [String(itemId)]: !prev[String(itemId)] }));
                  }}
                  encounterCheckedOut={isEncounterCheckedOut}
                  canDraftDecisions={canDraftDecisions}
                  draftDecision={itemId != null ? decisionDrafts[String(itemId)] : undefined}
                  failedAfterSubmit={itemId != null && failedDecisionIds.includes(String(itemId))}
                  onDecisionChange={handleDecisionDraftChange}
                  onDecisionClear={handleClearDecision}
                  aiDecision={
                    itemId != null
                      ? (saaspro.decisionsByItem[String(itemId)] || item?.ai_decision || null)
                      : (item?.ai_decision || null)
                  }
                  aiAwaitingReview={
                    itemId != null
                    && !(saaspro.decisionsByItem[String(itemId)] || item?.ai_decision)
                    && saaspro.loaded
                    && !saaspro.allMissing
                    && saaspro.hasPending
                  }
                  aiLoaded={saaspro.loaded}
                />
              );
            })}

            <div className="pa-review-items__foot">
              <span>Shortcuts: <kbd>J K</kbd> move · <kbd>A</kbd> accept · <kbd>R</kbd> reject</span>
              <Space wrap>
                {canDraftDecisions ? (
                  <>
                    <Button onClick={handleMarkAllAccepted} disabled={batchSubmitting || !hasPendingPaItems}>
                      Accept all
                    </Button>
                    <Button danger onClick={handleMarkAllRejected} disabled={batchSubmitting || !hasPendingPaItems}>
                      Reject all
                    </Button>
                  </>
                ) : null}
                <Button onClick={handleClearAllDecisions} disabled={batchSubmitting || !Object.keys(decisionDrafts || {}).length}>
                  Clear all decisions
                </Button>
              </Space>
            </div>
          </section>
        </main>

        <aside className="pa-review-aside">
          <section className="pa-review-card pa-review-totals">
            <div className="pa-review-kicker">APPROVED TOTAL</div>
            <div className="pa-review-totals__amount">{formatReviewNaira(draftSummary.approvedTotal)}</div>
            <div className="pa-review-totals__sub">
              of <b>{formatReviewNaira(requestedTotal)}</b> requested · {withheld > 0 ? `${formatReviewNaira(withheld)} withheld` : 'full amount'}
            </div>
            <div className="pa-review-bar" aria-hidden>
              <div className="pa-review-bar__accept" style={{ width: `${draftSummary.itemCount ? (draftSummary.accepted / draftSummary.itemCount) * 100 : 0}%` }} />
              <div className="pa-review-bar__reject" style={{ width: `${draftSummary.itemCount ? (draftSummary.rejected / draftSummary.itemCount) * 100 : 0}%` }} />
            </div>
            <div className="pa-review-legend">
              <div><i style={{ background: '#3e9e62' }} />{draftSummary.accepted} accepted</div>
              <div><i style={{ background: '#d8564f' }} />{draftSummary.rejected} rejected</div>
              <div><i style={{ background: '#dde2e9' }} />{draftSummary.pending} pending</div>
            </div>
            {canDraftDecisions ? (
              <TextArea
                value={overallComment}
                onChange={(e) => setOverallComment(e.target.value)}
                placeholder="Note for this submission — also used as the reject reason for items without their own note"
                disabled={batchSubmitting}
              />
            ) : null}
            <button
              type="button"
              className="pa-review-submit"
              onClick={handleSubmitBatchDecisions}
              disabled={!canSubmit}
            >
              {batchSubmitting
                ? 'Submitting…'
                : failedDecisionIds.length
                  ? `Retry failed (${failedDecisionIds.length})`
                  : `Submit ${decidedCount} decision${decidedCount === 1 ? '' : 's'}`}
            </button>
            <div className="pa-review-submit-hint">
              {isEncounterCheckedOut
                ? 'Encounter is checked out'
                : draftSummary.pending > 0
                  ? `${draftSummary.pending} item${draftSummary.pending === 1 ? '' : 's'} still need a decision`
                  : 'One batch submission, no partial sends'}
            </div>
            {canAddMorePa ? (
              <div style={{ marginTop: 10 }}>
                <PopConfirm
                  title={
                    <>
                      <strong>NB: This operation has financial implication!</strong>
                      <br />
                      <span>Are you sure you want to add more PAs to this encounter?</span>
                    </>
                  }
                  onConfirm={handleAddItems}
                  confirmType="danger"
                  triggerButtonText="Add more PAs"
                  triggerButtonProps={{ type: 'default', block: true }}
                  placement="top"
                  theme="danger"
                  debounceTime={500}
                />
              </div>
            ) : null}
          </section>

          <section className="pa-review-card pa-review-policy">
            <div className="pa-review-kicker">POLICY</div>
            <div className="pa-review-policy__rows">
              <div><span>Policy no.</span><span className="pa-review-policy__mono">{client?.policy_no || client?.policyNo || '—'}</span></div>
              <div><span>Member no.</span><span className="pa-review-policy__mono">{insuranceNo || '—'}</span></div>
              <div><span>Plan</span><span>{planLabel}</span></div>
              <div><span>Submitted</span><span>{submittedAt || '—'}</span></div>
            </div>
            <div className="pa-review-policy__actions">
              <PAProcessingHistory checkinId={checkinId || client?.code} />
              {PA_ENCOUNTER_COLLAB_DEFAULT && encounterCollabEnabled ? (
                <EncounterCollaborationControls
                  checkinId={checkinId || client?.code}
                  items={items}
                  disabled={batchSubmitting}
                  itemFlagState={itemFlagState}
                />
              ) : null}
              {hasMemberId ? (
                <Button
                  className="pa-action-overview__encounters"
                  onClick={() => navigator(`/checkins?insurance_no=${encodeURIComponent(memberId)}`)}
                >
                  Encounters
                </Button>
              ) : null}
            </div>
          </section>

          {/* The aside is a 344px column; the panel is a full-width card in it,
              never a flex item in the policy action row (it overflowed there). */}
          {/* Same consumption breakdown as the encounter details and claims
              screens: the envelope, every benefit the encounter touches with
              its limit (in the limit's unit), and per-line mapping. */}
          <section className="pa-review-card">
            <PmMappingPanel
              checkinCode={checkinId || client?.code}
              source="pa"
              title="Consumption & benefit mapping"
              refreshKey={pmPanelRefresh}
              consumptionFirst
              onMapped={() => setPmPanelRefresh((n) => n + 1)}
            />
          </section>
          <PmPaPanel
            checkSummaryOnly
            checkinCode={checkinId || client?.code}
            refreshKey={pmPanelRefresh}
            lineLabels={(items || []).reduce((acc, it, ix) => {
              const id = getPaLineItemId(it);
              if (id) acc[id] = { index: ix + 1, name: paLineItemName(it) };
              return acc;
            }, {})}
          />
        </aside>
      </div>

      <div className="pa-review-mobile-bar">
        <div>
          <strong>{formatReviewNaira(draftSummary.approvedTotal)}</strong>
          <div className="pa-review-submit-hint" style={{ marginTop: 0, textAlign: 'left' }}>
            {draftSummary.pending > 0 ? `${draftSummary.pending} still pending` : 'Ready to submit'}
          </div>
        </div>
        <Button type="primary" onClick={handleSubmitBatchDecisions} loading={batchSubmitting} disabled={!canSubmit}>
          Submit
        </Button>
      </div>

      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        title={`Diagnosis codes (${diagnosisCodes.length})`}
        open={diagnosisModalOpen}
        onCancel={() => setDiagnosisModalOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setDiagnosisModalOpen(false)}>
            Close
          </Button>
        ]}
        destroyOnClose
        width={640}
      >
        <div className="pa-action-diagnosis-modal-list">
          {diagnosisCodes.length ? diagnosisCodes.map((diag, idx) => (
            <div className="pa-diagnosis-item" key={`modal-${diag.code}-${idx}`}>
              <Tag color="blue">{diag.code}</Tag>
              <span>{diag.description}</span>
            </div>
          )) : <Empty description="No diagnosis attached" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        </div>
      </Modal>
    </div>
  )
}


const EncounterCollaborationControls = memo(({ checkinId, items = [], disabled = false, itemFlagState = null }) => {
  const { hasPermission } = usePermissions();
  const canCollaborate = hasPermission('preauth.read');
  const [queryOpen, setQueryOpen] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [querySending, setQuerySending] = useState(false);
  const [queryMessages, setQueryMessages] = useState([]);
  const [queryText, setQueryText] = useState('');
  const [queryItemRefs, setQueryItemRefs] = useState([]);
  const [queryUnreadCount, setQueryUnreadCount] = useState(0);

  const [flagOpen, setFlagOpen] = useState(false);
  const [flagLoading, setFlagLoading] = useState(false);
  const [flags, setFlags] = useState([]);
  const [flagType, setFlagType] = useState(undefined);
  const [flagReason, setFlagReason] = useState('');
  const [flagItemRefs, setFlagItemRefs] = useState([]);
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [flagActionLoadingId, setFlagActionLoadingId] = useState(0);
  const [flagResolutionNotes, setFlagResolutionNotes] = useState('');
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);

  const itemOptions = (items || []).map((item) => {
    const itemId = getPaLineItemId(item);
    return {
      value: String(itemId),
      label: `${item?.item_code || item?.code || itemId} - ${paLineItemName(item)}`,
    };
  }).filter((opt) => opt.value && opt.value !== 'null' && opt.value !== 'undefined');

  const loadQueryUnreadCount = useCallback(async () => {
    if (!checkinId) return;
    try {
      const resp = await lib.getPAEncounterQueryUnreadCount(checkinId);
      setQueryUnreadCount(Number(resp?.data?.data?.unread_count || 0));
    } catch (_e) {
      setQueryUnreadCount(0);
    }
  }, [checkinId]);

  const loadQueryMessages = useCallback(async () => {
    if (!checkinId) return;
    setQueryLoading(true);
    try {
      const resp = await lib.getPAEncounterQueries(checkinId);
      setQueryMessages(resp?.data?.data?.messages || []);
    } catch (_e) {
      notification.error({ message: 'Failed to load encounter query thread', placement: 'topLeft' });
    } finally {
      setQueryLoading(false);
    }
  }, [checkinId]);

  const openQueryDrawer = async () => {
    if (!checkinId || !canCollaborate) return;
    setQueryOpen(true);
    await Promise.all([
      loadQueryMessages(),
      loadQueryUnreadCount(),
      lib.markPAEncounterQueriesRead(checkinId, { mark_all_read: true }).catch(() => null),
    ]);
    setQueryUnreadCount(0);
  };

  const submitEncounterQuery = async () => {
    const content = queryText?.trim();
    if (!content || !checkinId) return;
    setQuerySending(true);
    try {
      const idempotencyKey = `enc-query-${checkinId}-${Date.now()}`;
      await lib.createPAEncounterQuery(checkinId, {
        content,
        item_refs: (queryItemRefs || []).map((id) => ({ item_id: Number(id) })),
      }, { idempotencyKey });
      setQueryText('');
      setQueryItemRefs([]);
      await loadQueryMessages();
      notification.success({ message: 'Query sent', placement: 'topLeft' });
    } catch (e) {
      notification.error({ message: e?.response?.data?.message || e?.message || 'Failed to send query', placement: 'topLeft' });
    } finally {
      setQuerySending(false);
    }
  };

  const loadEncounterFlags = useCallback(async () => {
    if (!checkinId) return;
    setFlagLoading(true);
    try {
      const resp = await lib.getPAEncounterFlags(checkinId, { flag_status: 'all' });
      setFlags(resp?.data?.data?.flags || []);
    } catch (_e) {
      notification.error({ message: 'Failed to load encounter flags', placement: 'topLeft' });
    } finally {
      setFlagLoading(false);
    }
  }, [checkinId]);

  const openFlagDrawer = async () => {
    if (!checkinId || !canCollaborate) return;
    setFlagOpen(true);
    await loadEncounterFlags();
  };

  const submitEncounterFlag = async () => {
    if (!checkinId || !flagType || !flagReason.trim()) return;
    setFlagSubmitting(true);
    try {
      const idempotencyKey = `enc-flag-${checkinId}-${Date.now()}`;
      await lib.createPAEncounterFlag(checkinId, {
        flag_type: flagType,
        flag_reason: flagReason.trim(),
        item_refs: (flagItemRefs || []).map((id) => ({ item_id: Number(id) })),
      }, { idempotencyKey });
      setFlagReason('');
      setFlagType(undefined);
      setFlagItemRefs([]);
      await loadEncounterFlags();
      itemFlagState?.reload();
      notification.success({ message: 'Encounter flag created', placement: 'topLeft' });
    } catch (e) {
      notification.error({ message: e?.response?.data?.message || e?.message || 'Failed to create flag', placement: 'topLeft' });
    } finally {
      setFlagSubmitting(false);
    }
  };

  const resolveFlag = async () => {
    if (!checkinId || !selectedFlag?.id) return;
    setFlagActionLoadingId(selectedFlag.id);
    try {
      await lib.resolvePAEncounterFlag(checkinId, selectedFlag.id, {
        resolution_notes: flagResolutionNotes || undefined,
      });
      setResolveOpen(false);
      setSelectedFlag(null);
      setFlagResolutionNotes('');
      await loadEncounterFlags();
      itemFlagState?.reload();
      notification.success({ message: 'Flag resolved', placement: 'topLeft' });
    } catch (e) {
      notification.error({ message: e?.response?.data?.message || e?.message || 'Failed to resolve flag', placement: 'topLeft' });
    } finally {
      setFlagActionLoadingId(0);
    }
  };

  const dismissFlag = async () => {
    if (!checkinId || !selectedFlag?.id) return;
    setFlagActionLoadingId(selectedFlag.id);
    try {
      await lib.dismissPAEncounterFlag(checkinId, selectedFlag.id, {
        resolution_notes: flagResolutionNotes || undefined,
      });
      setDismissOpen(false);
      setSelectedFlag(null);
      setFlagResolutionNotes('');
      await loadEncounterFlags();
      itemFlagState?.reload();
      notification.success({ message: 'Flag dismissed', placement: 'topLeft' });
    } catch (e) {
      notification.error({ message: e?.response?.data?.message || e?.message || 'Failed to dismiss flag', placement: 'topLeft' });
    } finally {
      setFlagActionLoadingId(0);
    }
  };

  useEffect(() => {
    loadQueryUnreadCount();
  }, [loadQueryUnreadCount]);

  // Badge before the drawer is opened: every active flag on the encounter, item and
  // encounter level, counted once (an encounter flag pinned to two items is one flag).
  const activeFlagCount = itemFlagState
    ? new Set((itemFlagState.flags || []).filter((f) => f.status === 'active').map((f) => `${f.source}:${f.flag_id}`)).size
    : (flags || []).filter((f) => String(f?.flag_status || '').toLowerCase() === 'active').length;
  const flagTypeOptions = [
    'manual_review',
    'cost_discrepancy',
    'duplicate_item',
    'policy_violation',
    'document_missing',
    'fraud_risk',
  ].map((value) => ({
    value,
    label: value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
  }));

  if (!canCollaborate) return null;

  return (
    <>
      <div className="pa-collab-actions">
        {queryUnreadCount > 0 ? (
          <Badge count={queryUnreadCount} showZero={false}>
            <Button type="default" onClick={openQueryDrawer} disabled={disabled || !checkinId} icon={<QuestionCircleOutlined />}>
              Query
            </Button>
          </Badge>
        ) : (
          <Button type="default" onClick={openQueryDrawer} disabled={disabled || !checkinId} icon={<QuestionCircleOutlined />}>
            Query
          </Button>
        )}
        {activeFlagCount > 0 ? (
          <Badge count={activeFlagCount} showZero={false}>
            <Button type="default" onClick={openFlagDrawer} disabled={disabled || !checkinId} icon={<WarningOutlined />}>
              Flag
            </Button>
          </Badge>
        ) : (
          <Button type="default" onClick={openFlagDrawer} disabled={disabled || !checkinId} icon={<WarningOutlined />}>
            Flag
          </Button>
        )}
      </div>

      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        title={`Encounter query · ${checkinId}`}
        open={queryOpen}
        onCancel={() => setQueryOpen(false)}
        footer={null}
        width={620}
        destroyOnClose
      >
        <Spin spinning={queryLoading}>
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {!queryMessages?.length ? (
              <Empty description="No messages yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : queryMessages.map((m) => (
              <div key={m?.id} className="pa-collab-message">
                <div className="pa-collab-message-meta">
                  <strong>{m?.sender_type || 'user'}</strong>
                  <span>{m?.created_at ? new Date(m.created_at).toLocaleString() : ''}</span>
                </div>
                <div>{m?.content}</div>
                {Array.isArray(m?.item_refs) && m.item_refs.length > 0 && (
                  <div className="pa-collab-refs">
                    {m.item_refs.map((ref, idx) => <Tag key={`${ref?.item_id || idx}`}>Item #{ref?.item_id}</Tag>)}
                  </div>
                )}
              </div>
            ))}
          </Space>
        </Spin>
        <div className="pa-collab-composer">
          <Select
            mode="multiple"
            allowClear
            placeholder="Optionally quote item(s)"
            value={queryItemRefs}
            onChange={setQueryItemRefs}
            options={itemOptions}
            style={{ width: '100%', marginBottom: 8 }}
          />
          <TextArea
            rows={3}
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            placeholder="Write query message..."
          />
          <div className="pa-collab-composer-actions">
            <Button type="primary" onClick={submitEncounterQuery} loading={querySending} disabled={!queryText?.trim()}>
              Send Query
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        title={`Encounter flags · ${checkinId}`}
        open={flagOpen}
        onCancel={() => setFlagOpen(false)}
        footer={null}
        width={680}
        destroyOnClose
      >
        <div className="pa-collab-flag-form">
          <Select
            placeholder="Select flag type"
            value={flagType}
            onChange={setFlagType}
            options={flagTypeOptions}
            style={{ width: '100%', marginBottom: 8 }}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Optionally quote item(s)"
            value={flagItemRefs}
            onChange={setFlagItemRefs}
            options={itemOptions}
            style={{ width: '100%', marginBottom: 8 }}
          />
          <TextArea
            rows={3}
            value={flagReason}
            onChange={(e) => setFlagReason(e.target.value)}
            placeholder="Describe why this encounter should be flagged..."
          />
          <div className="pa-collab-composer-actions">
            <Button type="primary" icon={<WarningOutlined />} onClick={submitEncounterFlag} loading={flagSubmitting} disabled={!flagType || !flagReason.trim()}>
              Create Flag
            </Button>
          </div>
        </div>

        {itemFlagState?.flags?.length ? (
          <div style={{ marginTop: 14 }}>
            <h4 style={{ margin: '0 0 8px' }}>Flagged items</h4>
            <ItemFlagsList flags={itemFlagState.flags} showItem />
            <h4 style={{ margin: '16px 0 0' }}>Encounter flags</h4>
          </div>
        ) : null}
        <Spin spinning={flagLoading}>
          <Space direction="vertical" style={{ width: '100%', marginTop: 14 }} size={12}>
            {!flags?.length ? (
              <Empty description="No encounter flags yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : flags.map((f) => (
              <div key={f?.id} className="pa-collab-flag-item">
                <div className="pa-collab-flag-header">
                  <Space>
                    <Tag color={String(f?.flag_status || '').toLowerCase() === 'active' ? 'red' : 'green'}>
                      {String(f?.flag_status || 'active').toUpperCase()}
                    </Tag>
                    <Tag color="blue">{String(f?.flag_type || '').replace(/_/g, ' ')}</Tag>
                  </Space>
                  {String(f?.flag_status || '').toLowerCase() === 'active' && (
                    <Space>
                      <Button
                        size="small"
                        type="primary"
                        loading={flagActionLoadingId === f?.id}
                        onClick={() => { setSelectedFlag(f); setResolveOpen(true); }}
                      >
                        Resolve
                      </Button>
                      <Button
                        size="small"
                        danger
                        loading={flagActionLoadingId === f?.id}
                        onClick={() => { setSelectedFlag(f); setDismissOpen(true); }}
                      >
                        Dismiss
                      </Button>
                    </Space>
                  )}
                </div>
                <div>{f?.flag_reason || '-'}</div>
                {Array.isArray(f?.item_refs) && f.item_refs.length > 0 && (
                  <div className="pa-collab-refs">
                    {f.item_refs.map((ref, idx) => <Tag key={`${ref?.item_id || idx}`}>Item #{ref?.item_id}</Tag>)}
                  </div>
                )}
              </div>
            ))}
          </Space>
        </Spin>
      </Modal>

      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        open={resolveOpen}
        onCancel={() => { setResolveOpen(false); setSelectedFlag(null); setFlagResolutionNotes(''); }}
        onOk={resolveFlag}
        okButtonProps={{ loading: flagActionLoadingId > 0 }}
        title="Resolve flag"
      >
        <TextArea rows={4} value={flagResolutionNotes} onChange={(e) => setFlagResolutionNotes(e.target.value)} placeholder="Resolution notes (optional)" />
      </Modal>

      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        open={dismissOpen}
        onCancel={() => { setDismissOpen(false); setSelectedFlag(null); setFlagResolutionNotes(''); }}
        onOk={dismissFlag}
        okButtonProps={{ loading: flagActionLoadingId > 0, danger: true }}
        title="Dismiss flag"
      >
        <TextArea rows={4} value={flagResolutionNotes} onChange={(e) => setFlagResolutionNotes(e.target.value)} placeholder="Dismissal notes (optional)" />
      </Modal>
    </>
  );
});

const AuthorizationRequestDataListHook = memo((props) => {
  const [visibleComments, setVisibleComments] = useState(false);
  const [visibleDetails, setVisibleDetails] = useState(false);
  const itemId = props?.itemId ?? getPaLineItemId(props?.data);
  // Flags on this item (facility, enrollee, staff), from the encounter's item-flag list.
  const itemFlags = props?.itemFlags || [];
  const hasActiveFlag = itemFlags.some((f) => f.status === 'active') || Boolean(props?.data?.has_active_flags);
  const rowStatus = getPaLineItemStatus(props?.data);
  /** Must be a real boolean — `{0 && <Node/>}` renders the digit `0` in React. */
  const isFinalized = rowStatus === 1 || rowStatus === 3;
  const isActionable = isPaLineItemActionable(props?.data);
  const canEdit = Boolean(props?.canDraftDecisions && isActionable);
  const draft = props?.draftDecision || {};
  const selectedDecision = draft?.decision;
  /** rc-segmented defaults internal state to the first option when `value` is `undefined`, so Accept looks selected while React has no draft—clicks on Accept then do not fire. Use `null` for "no selection" so both segments are clickable. */
  const segmentedDecisionValue = selectedDecision === 'accept' || selectedDecision === 'reject' ? selectedDecision : null;

  const approvedCost = roundMoney(selectedDecision === 'accept'
    ? Number(draft?.approved_cost || 0)
    : Number(props?.data?.approved_cost || 0));
  const showApprovedEditors = canEdit && selectedDecision === 'accept';
  const statusLabel = rowStatus === 1 ? 'Approved' : rowStatus === 3 ? 'Rejected' : rowStatus === 2 ? 'Queried' : 'Pending';
  const statusChipKey = rowStatus === 1 ? 'approved' : rowStatus === 3 ? 'rejected' : rowStatus === 2 ? 'queried' : 'pending';
  const stopRowClick = (e) => e?.stopPropagation?.();

  const handleCommentModalClose = () => {
    setVisibleComments(false);
  };

  const d = props?.data || {};
  const qtySafe = Math.max(1, Number(d.quantity) || 1);
  const requestedCost = roundMoney(Number(d.cost || 0));
  const draftApprovedQty = selectedDecision === 'accept'
    ? Math.max(1, Number(draft?.approved_quantity) > 0 ? Number(draft.approved_quantity) : qtySafe)
    : (d.approved_quantity != null && d.approved_quantity !== ''
      ? Math.max(1, Number(d.approved_quantity) || qtySafe)
      : qtySafe);
  const unitCostDisplay = roundMoney(d.unit_cost != null && d.unit_cost !== ''
    ? Number(d.unit_cost)
    : Number(d.cost || 0) / qtySafe);
  const unitApprovedStored = d.unit_approved_cost != null && d.unit_approved_cost !== ''
    ? Number(d.unit_approved_cost)
    : null;
  const unitApprovedDisplay = roundMoney(unitApprovedStored != null && Number.isFinite(unitApprovedStored)
    ? unitApprovedStored
    : (Number(d.approved_cost || 0) / draftApprovedQty));

  /** Stable unit while drafting accept: prefer stored unit, else cost ÷ current approved qty. */
  const draftUnitApproved = (() => {
    if (unitApprovedStored != null && Number.isFinite(unitApprovedStored) && unitApprovedStored > 0) {
      return roundMoney(unitApprovedStored);
    }
    const currentQty = Math.max(1, Number(draft?.approved_quantity) > 0 ? Number(draft.approved_quantity) : qtySafe);
    const currentCost = Number(draft?.approved_cost || 0);
    if (currentCost > 0) return roundMoney(currentCost / currentQty);
    return roundMoney(Number(d.cost || 0) / qtySafe);
  })();

  const showApprovedReadOnly = !showApprovedEditors && (
    selectedDecision === 'accept'
    || rowStatus === 1
    || (!canEdit && Number(d.approved_cost || 0) > 0 && rowStatus !== 3)
  );
  const hideApprovedColumn = selectedDecision === 'reject' || rowStatus === 3
    || (canEdit && !selectedDecision);

  const displayApprovedQty = showApprovedEditors || showApprovedReadOnly ? draftApprovedQty : null;
  const displayApprovedCost = showApprovedEditors || showApprovedReadOnly ? approvedCost : null;
  const approvalDiff = (displayApprovedQty != null && displayApprovedCost != null)
    ? formatApprovalDiff(displayApprovedQty, displayApprovedCost, qtySafe, requestedCost)
    : null;
  const isOverApprove = Boolean(approvalDiff?.overApprove);

  const renderMoney = (value, { strong = false } = {}) => {
    const node = (
      <CurrencyFormat
        value={roundMoney(value || 0)}
        displayType="text"
        thousandSeparator
        prefix="₦"
        decimalScale={2}
        fixedDecimalScale
      />
    );
    return strong ? <strong>{node}</strong> : node;
  };

  const renderApprovalDiff = () => {
    if (!approvalDiff) return null;
    return (
      <div
        className={`pa-batch-diff${approvalDiff.isSame ? ' pa-batch-diff--same' : ''}${isOverApprove ? ' pa-batch-diff--over' : ''}`}
      >
        {isOverApprove ? <WarningOutlined className="pa-batch-diff__warn-icon" aria-hidden /> : null}
        <span>{approvalDiff.label}</span>
      </div>
    );
  };

  const showApprovedFigures = selectedDecision === 'accept' || rowStatus === 1;
  const showRejectedState = selectedDecision === 'reject' || rowStatus === 3;

  const rowClassName = [
    'pa-review-row',
    props?.compact ? 'pa-review-row--compact' : '',
    props?.failedAfterSubmit ? 'pa-review-row--failed' : '',
    showApprovedFigures ? 'pa-review-row--accept' : '',
    showRejectedState ? 'pa-review-row--reject' : '',
  ].filter(Boolean).join(' ');

  const decidedByLabel = rowStatus === 3 ? 'Rejected by' : 'Approved by';
  // The account that processed the line. "Reviewer" was an old placeholder
  // for a decision saved without its account; never show it as a name.
  const savedDecider = /^reviewer(\s*#\d+)?$/i.test(String(props?.data?.decided_by_name || '').trim())
    ? null
    : props?.data?.decided_by_name || null;
  const deciderName = props?.data?.processed_by || savedDecider;
  const decidedByValue = deciderName || props?.data?.decided_by_user_id
    ? [
        deciderName || null,
        props?.data?.decided_by_user_id && !/Staff #/.test(deciderName || '') ? `(Staff #${props.data.decided_by_user_id})` : null,
      ].filter(Boolean).join(' ')
    : 'Account not recorded';
  const auditComment = props?.data?.approval_comment
    || props?.data?.rejection_comment
    || props?.data?.query_comment
    || props?.data?.comment
    || null;
  const detailApprovedQty = selectedDecision === 'accept'
    ? draftApprovedQty
    : (props?.data?.approved_quantity != null && props?.data?.approved_quantity !== ''
      ? Math.max(1, Number(props.data.approved_quantity) || qtySafe)
      : qtySafe);
  const detailApprovedUnit = selectedDecision === 'accept' ? draftUnitApproved : unitApprovedDisplay;
  const clinicalLine = formatPaLineItemClinicalLine(props?.data);
  const filedAt = getPaLineItemFiledAt(props?.data);
  const receivedAt = getPaLineItemReceivedAt(props?.data) || filedAt;
  const processedAt = getPaLineItemProcessedAt(props?.data);
  // processed_by comes from the API (never the old "Reviewer" placeholder);
  // only an older API without it falls back to the saved name.
  const processedBy = Object.prototype.hasOwnProperty.call(props?.data || {}, 'processed_by')
    ? (props?.data?.processed_by || '')
    : (props?.data?.decided_by_name || '');
  const turnaround = formatPaLineItemTurnaround(props?.data);
  const rowMeta = [
    clinicalLine,
    receivedAt ? `Received ${formatPaTurnaroundTime(receivedAt)}` : null,
    processedAt ? `Processed ${formatPaTurnaroundTime(processedAt)}` : null,
  ].filter(Boolean).join(' · ');
  const clinicalDetailFields = paLineItemDetailFields(props?.data);

  const aiRecommendation = props?.aiDecision?.recommendation || null;
  const aiChipVariant = aiRecommendation === 'partial_approve' ? 'partial' : aiRecommendation;
  const aiChipLabel = aiRecommendation ? `AI ${recommendationLabel(aiRecommendation).toLowerCase()}` : null;
  const isFocused = Boolean(props?.isFocused);
  const rationaleOpen = Boolean(props?.rationaleOpen);
  const handleToggleRationale = (e) => {
    e?.stopPropagation?.();
    props?.onFocusRow?.();
    props?.onToggleRationale?.();
  };

  return (
    <>
      <div
        className={`${rowClassName}${isFocused ? ' is-focus' : ''}`}
        onClick={() => setVisibleDetails(true)}
        role="button"
        tabIndex={0}
      >
        <div className="pa-review-row__main">
          <div className="pa-review-row__name-row">
            <span className="pa-review-row__sn">{String(props?.serialNumber ?? '—').padStart(2, '0')}</span>
            <span className="pa-review-row__name">{paLineItemName(props?.data)}</span>
            <span className="pa-review-row__qty" title="Requested quantity">×{qtySafe}</span>
            {aiChipLabel ? (
              <span className={`pa-review-ai-chip pa-review-ai-chip--${aiChipVariant}`}>{aiChipLabel}</span>
            ) : props?.aiAwaitingReview ? (
              <span className="pa-review-ai-chip pa-review-ai-chip--pending">AI reviewing…</span>
            ) : null}
            {itemFlags.some((f) => f.status === 'active') ? (
              <span onClick={(e) => { e.stopPropagation(); setVisibleDetails(true); }}>
                <ItemFlagTag flags={itemFlags} />
              </span>
            ) : props?.data?.has_active_flags ? (
              <span className="pa-review-flag-chip">Flagged</span>
            ) : null}
          </div>
          {rowMeta ? <div className="pa-review-row__meta">{rowMeta}</div> : null}
          {rationaleOpen && props?.aiDecision?.rationale ? (
            <div className="pa-review-row__rationale">{props.aiDecision.rationale}</div>
          ) : null}
        </div>

        <div className="pa-review-money">
          <div className="pa-review-money__label">REQUESTED</div>
          <div className="pa-review-money__value">{renderMoney(requestedCost, { strong: true })}</div>
        </div>

        <div className="pa-review-money pa-review-money--approved" onClick={stopRowClick}>
          <div className="pa-review-money__label">APPROVED</div>
          {showApprovedEditors ? (
            <div className="pa-review-amount">
              <InputNumber
                min={1}
                precision={0}
                value={draftApprovedQty}
                aria-label="Approved quantity"
                onClick={stopRowClick}
                onChange={(value) => {
                  const nextQty = Math.max(1, Math.floor(Number(value) || 1));
                  props?.onDecisionChange?.(itemId, {
                    decision: 'accept',
                    approved_quantity: nextQty,
                    approved_cost: roundMoney(draftUnitApproved * nextQty),
                  });
                }}
                disabled={!canEdit}
              />
              <span>·</span>
              <InputNumber
                min={0}
                precision={2}
                value={approvedCost}
                aria-label="Approved total"
                onClick={stopRowClick}
                onChange={(value) => {
                  if (value < 0) return;
                  props?.onDecisionChange?.(itemId, {
                    decision: 'accept',
                    approved_quantity: draftApprovedQty,
                    approved_cost: roundMoney(value || 0),
                  });
                }}
                onBlur={(e) => {
                  const nextValue = roundMoney(e?.target?.value || 0);
                  if (!(nextValue > 0)) {
                    props?.onDecisionChange?.(itemId, {
                      decision: 'accept',
                      approved_quantity: draftApprovedQty,
                      approved_cost: roundMoney(draftUnitApproved * draftApprovedQty),
                    });
                  }
                }}
                disabled={!canEdit}
              />
              {renderApprovalDiff()}
            </div>
          ) : hideApprovedColumn ? (
            <div className={`pa-review-money__value${showRejectedState ? ' is-reject' : ' is-empty'}`}>
              {showRejectedState ? 'Rejected' : (canEdit && !selectedDecision ? 'Accept to set' : '—')}
            </div>
          ) : showApprovedReadOnly ? (
            <>
              <div className={`pa-review-money__value${showRejectedState ? ' is-reject' : ''}`}>
                {renderMoney(approvedCost, { strong: true })}
              </div>
              {renderApprovalDiff()}
            </>
          ) : (
            <div className="pa-review-money__value is-empty">—</div>
          )}
        </div>

        <div className="pa-review-row__actions" onClick={stopRowClick}>
          {canEdit ? (
            <div className="pa-review-decide">
              <button
                type="button"
                className={segmentedDecisionValue === 'accept' ? 'is-accept' : ''}
                onClick={() => {
                  const nextQty = Math.max(
                    1,
                    Number(draft?.approved_quantity) > 0 ? Number(draft.approved_quantity) : qtySafe
                  );
                  const unit = draftUnitApproved > 0
                    ? draftUnitApproved
                    : roundMoney(Number(d.cost || 0) / qtySafe);
                  const fallbackCost = roundMoney(
                    Number(draft?.approved_cost) > 0
                      ? Number(draft.approved_cost)
                      : unit * nextQty
                  );
                  props?.onDecisionChange?.(itemId, {
                    decision: 'accept',
                    approved_quantity: nextQty,
                    approved_cost: fallbackCost,
                  });
                }}
              >
                Accept
              </button>
              <button
                type="button"
                className={segmentedDecisionValue === 'reject' ? 'is-reject' : ''}
                onClick={() => {
                  props?.onDecisionChange?.(itemId, {
                    decision: 'reject',
                    approved_cost: undefined,
                    approved_quantity: undefined,
                  });
                }}
              >
                Reject
              </button>
            </div>
          ) : null}
          <button
            type="button"
            className={`pa-review-icon-btn${isFocused ? ' is-on' : ''}`}
            onClick={handleToggleRationale}
            title="AI rationale"
            aria-expanded={rationaleOpen}
          >
            i
          </button>
          <button
            type="button"
            className={`pa-review-icon-btn${hasActiveFlag ? ' is-flag' : ''}`}
            onClick={(e) => {
              stopRowClick(e);
              setVisibleDetails(true);
            }}
            title="Flag"
          >
            <WarningOutlined />
          </button>
        </div>
      </div>

      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog pa-item-detail-modal`}
        title={
          <div className="pa-item-detail-header">
            <div className="pa-item-detail-header__text">
              <span className="pa-item-detail-header__eyebrow">Item details</span>
              <span className="pa-item-detail-header__name">{paLineItemName(props?.data)}</span>
              {clinicalLine ? (
                <span className="pa-item-detail-header__clinical">{clinicalLine}</span>
              ) : null}
            </div>
            <span className={`pa-batch-status-chip pa-batch-status-chip--${statusChipKey}`}>
              {isFinalized && rowStatus === 1 ? <CheckOutlined /> : null}
              {isFinalized && rowStatus === 3 ? <CloseOutlined /> : null}
              {statusLabel}
            </span>
          </div>
        }
        open={visibleDetails}
        onCancel={() => setVisibleDetails(false)}
        footer={[
          <Button key="comments" onClick={() => { setVisibleDetails(false); setVisibleComments(true); }}>
            View Comments
          </Button>,
          <Button key="close" type="primary" onClick={() => setVisibleDetails(false)}>
            Close
          </Button>
        ]}
        destroyOnClose
        width={560}
      >
        <div className="pa-item-detail">
          {clinicalDetailFields.length ? (
            <section className="pa-item-detail-section">
              <h4 className="pa-item-detail-section__title">Product</h4>
              <dl className="pa-item-detail-grid">
                {clinicalDetailFields.map((field) => (
                  <div className="pa-item-detail-row" key={`${field.label}-${field.value}`}>
                    <dt>{field.label}</dt>
                    <dd>{field.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null}

          <section className="pa-item-detail-section">
            <h4 className="pa-item-detail-section__title">Requested</h4>
            <dl className="pa-item-detail-grid">
              <div className="pa-item-detail-row">
                <dt>Quantity</dt>
                <dd>{qtySafe}</dd>
              </div>
              <div className="pa-item-detail-row">
                <dt>Unit cost</dt>
                <dd>{renderMoney(unitCostDisplay)}</dd>
              </div>
              <div className="pa-item-detail-row">
                <dt>Total</dt>
                <dd>{renderMoney(requestedCost, { strong: true })}</dd>
              </div>
              <div className="pa-item-detail-row">
                <dt>Received</dt>
                <dd>{formatPaTurnaroundTime(receivedAt)}</dd>
              </div>
              <div className="pa-item-detail-row">
                <dt>Processed</dt>
                <dd>
                  {processedAt
                    ? `${formatPaTurnaroundTime(processedAt)}${processedBy ? ` · ${processedBy}` : ''}`
                    : 'Pending'}
                </dd>
              </div>
              <div className="pa-item-detail-row">
                <dt>Turnaround</dt>
                <dd>{processedAt ? (turnaround || '—') : 'Pending'}</dd>
              </div>
              <div className="pa-item-detail-row">
                <dt>Category</dt>
                <dd>{props?.data?.category_name || '—'}</dd>
              </div>
            </dl>
          </section>

          <section className="pa-item-detail-section">
            <h4 className="pa-item-detail-section__title">Approved</h4>
            {showApprovedFigures ? (
              <dl className="pa-item-detail-grid">
                <div className="pa-item-detail-row">
                  <dt>Quantity</dt>
                  <dd>{detailApprovedQty}</dd>
                </div>
                <div className="pa-item-detail-row">
                  <dt>Unit</dt>
                  <dd>{renderMoney(detailApprovedUnit)}</dd>
                </div>
                <div className="pa-item-detail-row">
                  <dt>Total</dt>
                  <dd>{renderMoney(approvedCost, { strong: true })}</dd>
                </div>
                {approvalDiff ? (
                  <div className="pa-item-detail-row pa-item-detail-row--full">
                    <dt>vs requested</dt>
                    <dd>{renderApprovalDiff()}</dd>
                  </div>
                ) : null}
              </dl>
            ) : showRejectedState ? (
              <p className="pa-item-detail-empty">Rejected — no approved amounts</p>
            ) : (
              <p className="pa-item-detail-empty">Not decided yet</p>
            )}
          </section>

          <section className="pa-item-detail-section pa-item-detail-section--ai">
            <h4 className="pa-item-detail-section__title">AI</h4>
            {(props?.aiDecision || props?.aiAwaitingReview || props?.aiLoaded === false) ? (
              <SaaSProAdvisoryChip
                decision={props?.aiDecision}
                requestedCost={props?.data?.cost}
                forceExpanded
                pendingLabel="Awaiting AI…"
              />
            ) : (
              <p className="pa-item-detail-empty">No AI advisory for this item</p>
            )}
          </section>

          <section className="pa-item-detail-section">
            <h4 className="pa-item-detail-section__title">Audit</h4>
            <dl className="pa-item-detail-grid">
              {isFinalized ? (
                <div className="pa-item-detail-row">
                  <dt>{decidedByLabel}</dt>
                  <dd>{decidedByValue}</dd>
                </div>
              ) : null}
              <div className="pa-item-detail-row pa-item-detail-row--full">
                <dt>Comments</dt>
                <dd className={auditComment ? undefined : 'pa-item-detail-muted'}>
                  {auditComment || 'None yet'}
                </dd>
              </div>
            </dl>
          </section>

          {(hasActiveFlag || itemFlags.length) ? (
            <section className="pa-item-detail-section">
              <h4 className="pa-item-detail-section__title">Flags</h4>
              <ItemFlagsList flags={itemFlags} emptyText="No flags on this item" />
            </section>
          ) : null}

          {canEdit ? (
            <section className="pa-item-detail-section pa-item-detail-section--note">
              <label className="pa-item-detail-section__title" htmlFor={`pa-item-detail-note-${itemId}`}>
                Decision note
                <span className="pa-item-detail-optional">optional</span>
              </label>
              <TextArea
                id={`pa-item-detail-note-${itemId}`}
                rows={3}
                value={draft?.comment || ''}
                placeholder="Add a note for this decision"
                onChange={(e) => props?.onDecisionChange?.(itemId, { comment: e?.target?.value || '' })}
              />
            </section>
          ) : null}
        </div>
      </Modal>

      <Modal
        rootClassName={ADMIN_REVIEW_MODAL_CLASS}
        className={`${ADMIN_REVIEW_MODAL_CLASS}__dialog`}
        title="Authorization item comments"
        open={visibleComments}
        onCancel={handleCommentModalClose}
        footer={[
          <Button key="close" onClick={handleCommentModalClose}>
            Close
          </Button>
        ]}
        destroyOnClose
        width={600}
      >
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {props?.data?.approval_comment && (
            <div style={{ marginBottom: '16px' }}>
              <Typography.Title level={5} style={{ color: 'var(--success-primary)', marginBottom: '8px' }}>
                <CheckOutlined style={{ marginRight: '8px' }} />
                Approval Comment
              </Typography.Title>
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(73, 184, 112, 0.1)',
                border: '1px solid rgba(73, 184, 112, 0.2)',
                borderRadius: '6px',
                borderLeft: '4px solid var(--success-primary)'
              }}>
                {props.data.approval_comment}
              </div>
            </div>
          )}

          {props?.data?.rejection_comment && (
            <div style={{ marginBottom: '16px' }}>
              <Typography.Title level={5} style={{ color: 'var(--error-primary)', marginBottom: '8px' }}>
                <CloseOutlined style={{ marginRight: '8px' }} />
                Rejection Comment
              </Typography.Title>
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(231, 76, 60, 0.1)',
                border: '1px solid rgba(231, 76, 60, 0.2)',
                borderRadius: '6px',
                borderLeft: '4px solid var(--error-primary)'
              }}>
                {props.data.rejection_comment}
              </div>
            </div>
          )}

          {props?.data?.query_comment && (
            <div style={{ marginBottom: '16px' }}>
              <Typography.Title level={5} style={{ color: 'var(--warning-primary)', marginBottom: '8px' }}>
                <QuestionCircleOutlined style={{ marginRight: '8px' }} />
                Query Comment
              </Typography.Title>
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(243, 156, 18, 0.1)',
                border: '1px solid rgba(243, 156, 18, 0.2)',
                borderRadius: '6px',
                borderLeft: '4px solid var(--warning-primary)'
              }}>
                {props.data.query_comment}
              </div>
            </div>
          )}

          {props?.data?.comment && (
            <div style={{ marginBottom: '16px' }}>
              <Typography.Title level={5} style={{ color: 'var(--brand-primary)', marginBottom: '8px' }}>
                <MessageOutlined style={{ marginRight: '8px' }} />
                General Comment
              </Typography.Title>
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(52, 151, 211, 0.1)',
                border: '1px solid rgba(52, 151, 211, 0.2)',
                borderRadius: '6px',
                borderLeft: '4px solid var(--brand-primary)'
              }}>
                {props.data.comment}
              </div>
            </div>
          )}

          {!props?.data?.approval_comment && !props?.data?.rejection_comment && !props?.data?.query_comment && !props?.data?.comment && (
            <Empty
              description="No comments available for this authorization item"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </div>
      </Modal>
    </>
  );
});

const PAFiling = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkinParam = searchParams.get('checkin');

  useEffect(() => {
    if (!checkinParam) navigate('/preauth');
  }, [checkinParam, navigate]);

  return checkinParam ? <PARequestHook /> : null;
}

export default PAFiling;